/* Mit dem Server verbundene Karte: automatisches Speichern, Konflikterkennung, Öffnen und Versionen.
 * Eine Karte ist erst „verbunden“, wenn sie vom Server geöffnet oder dorthin gespeichert wurde; nur dann wird automatisch gespeichert.
 * Ohne DOM: Text, Meldungen und Zeitgeber werden übergeben, damit sich alles mit node prüfen lässt. */
import type { BackendClient, MapAccess, MapMeta } from './backend.ts';

/** Verbundene Karte: ID, aktueller Revisionszähler (rev) und Name auf dem Server */
export interface CloudMap { id: string; rev: number; name: string; access?: MapAccess }

/** Karte nur lesbar (lesend freigegeben): es wird nie gespeichert */
export const isReadOnly = (m: { access?: MapAccess } | null): boolean => !!m && m.access === 'read';

export interface CloudSnapshot {
  version: number;
  link: CloudMap | null;
  /** Automatisches Speichern pausiert (Konflikt oder zu große Karte) */
  paused: boolean;
  /** Es gibt einen Konflikt, über den der Benutzer entscheiden muss */
  conflict: { serverRev: number } | null;
  /** Angemeldet? Ohne Anmeldung wird nicht gespeichert */
  authed: boolean;
}

export interface CloudDeps {
  /** Aktueller Client oder null, solange keine Verbindung besteht */
  client: () => BackendClient | null;
  /** Text der aktuellen Karte (inklusive Gruppen) */
  getText: () => string;
  /** Eine geladene BSP lässt sich nicht als Karte speichern */
  isBsp: () => boolean;
  /** Karte in den Editor laden; muss dabei docLoading() vor und docLoaded() nach dem Laden aufrufen */
  loadText: (text: string, name: string, label: string) => void;
  /** Übersetzung (Schlüssel, Werte) und Statusmeldung */
  t: (key: string, params?: Record<string, unknown>) => string;
  say: (msg: string) => void;
  setTimer?: (f: () => void, ms: number) => unknown;
  clearTimer?: (t: unknown) => void;
  /** Wartezeit nach der letzten Änderung (ms) */
  delay?: number;
}

export class CloudLink {
  private deps: CloudDeps;
  private link: CloudMap | null = null;
  private pending: CloudMap | null = null;
  private paused = false;
  private conflict: { serverRev: number } | null = null;
  private saving = false;
  private again = false;
  private timer: unknown = null;
  private version = 0;
  private snap: CloudSnapshot;
  private listeners = new Set<() => void>();

  constructor(deps: CloudDeps) { this.deps = deps; this.snap = this.build(); }

  private build(): CloudSnapshot {
    const c = this.deps.client();
    return { version: this.version, link: this.link, paused: this.paused, conflict: this.conflict, authed: !!(c && c.token) };
  }
  private emit(): void {
    const n = this.build(), a = this.snap;
    if (n.link === a.link && n.paused === a.paused && n.conflict === a.conflict && n.authed === a.authed) return;
    this.version++; n.version = this.version; this.snap = n;
    this.listeners.forEach((f) => f());
  }
  subscribe = (fn: () => void): (() => void) => { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; };
  getSnapshot = (): CloudSnapshot => this.snap;
  /** Neu bewerten, z. B. nach Anmeldung oder Abmeldung */
  refresh(): void { this.emit(); }

  private setT = (f: () => void, ms: number): unknown => (this.deps.setTimer ? this.deps.setTimer(f, ms) : setTimeout(f, ms));
  private clearT = (t: unknown): void => { if (this.deps.clearTimer) this.deps.clearTimer(t); else clearTimeout(t as ReturnType<typeof setTimeout>); };
  private stopTimer(): void { if (this.timer) { this.clearT(this.timer); this.timer = null; } }

  // ---- Laden einer Karte in den Editor: nur open/openRevision verbinden, jedes andere Laden löst die Verbindung
  /** Vor dem Laden eines Dokuments aufrufen: merkt sich die Verbindung, die das neue Dokument bekommen soll */
  docLoading(): void {
    this.link = this.pending; this.pending = null;
    this.stopTimer(); this.paused = false; this.conflict = null;
    this.emit();
  }
  /** Nach dem Laden: das Laden selbst ist kein Grund zum Speichern */
  docLoaded(): void { this.stopTimer(); this.emit(); }

  async open(id: string): Promise<CloudMap> {
    const client = this.deps.client() as BackendClient;
    const m = await client.mapGet(id);
    this.pending = { id: m.id, rev: m.rev, name: m.name, access: m.access };
    this.deps.loadText(m.text, m.name, this.deps.t('cloud.open_label'));
    this.deps.say(this.deps.t(isReadOnly(m) ? 'cloud.opened_ro' : 'cloud.opened', { name: m.name, rev: m.rev }));
    return { id: m.id, rev: m.rev, name: m.name, access: m.access };
  }
  /** Frühere Version in den Editor laden; die Karte bleibt verbunden, das nächste Speichern erzeugt eine neue Version */
  async openRevision(meta: { id: string; rev: number; name: string; access?: MapAccess }, rev: number): Promise<void> {
    const client = this.deps.client() as BackendClient;
    const r = await client.mapRevision(meta.id, rev);
    const cur = this.link && this.link.id === meta.id ? this.link : meta;
    this.pending = { id: cur.id, rev: cur.rev, name: cur.name, access: 'access' in cur ? cur.access : undefined };
    this.deps.loadText(r.text, cur.name, this.deps.t('cloud.revision_label', { rev }));
    this.deps.say(this.deps.t('cloud.revision_loaded', { rev }));
  }

  /** Aktuelle Karte als neue Karte speichern und verbinden */
  async createFromCurrent(name: string): Promise<MapMeta> {
    const client = this.deps.client() as BackendClient;
    const m = await client.mapCreate(name, this.deps.getText());
    this.link = { id: m.id, rev: m.rev, name: m.name, access: 'owner' }; this.paused = false; this.conflict = null;
    this.deps.say(this.deps.t('cloud.saved', { name: m.name, rev: m.rev }));
    this.emit();
    return m;
  }

  // ---- Automatisches Speichern
  /** Bei jeder Änderung des Dokuments aufrufen */
  schedule(): void {
    const c = this.deps.client();
    if (!this.link || isReadOnly(this.link) || this.paused || this.deps.isBsp() || !(c && c.token)) return;
    this.stopTimer();
    this.timer = this.setT(() => { this.timer = null; void this.save(); }, this.deps.delay ?? 4000);
  }

  /** Speichert den aktuellen Stand. Bei einem Konflikt (andere Änderung seit dem Laden) wird nichts überschrieben. */
  async save(): Promise<boolean> {
    const c = this.link, cl = this.deps.client();
    if (!c || !cl || !cl.token || this.deps.isBsp() || isReadOnly(c)) return false;
    if (this.saving) { this.again = true; return false; }
    this.saving = true; this.stopTimer();
    try {
      const meta = await cl.mapSave(c.id, c.rev, { text: this.deps.getText() });
      if (this.link === c) {
        this.link = { id: c.id, rev: meta.rev, name: meta.name, access: c.access }; this.paused = false;
        this.deps.say(this.deps.t('cloud.saved', { name: meta.name, rev: meta.rev }));
      }
      return true;
    } catch (e) {
      const err = e as { status?: number; message?: string; data?: { rev?: number } | null };
      if (err.status === 409 && err.data && err.data.rev) { this.paused = true; this.conflict = { serverRev: err.data.rev }; }
      else if (err.status === 404) { this.link = null; this.deps.say(this.deps.t('cloud.gone')); }
      else if (err.status === 413) { this.paused = true; this.deps.say(this.deps.t('cloud.too_big', { msg: err.message })); }
      else this.deps.say(this.deps.t('cloud.save_failed', { msg: err.message }));
      return false;
    } finally {
      this.saving = false; this.emit();
      if (this.again) { this.again = false; this.schedule(); }
    }
  }

  // ---- Konfliktlösung
  /** „Später entscheiden“: Dialog schließen, Speichern bleibt pausiert */
  dismissConflict(): void { this.conflict = null; this.emit(); }
  /** Serverstand mit dem eigenen überschreiben */
  async overwrite(): Promise<boolean> {
    if (!this.link || !this.conflict) return false;
    this.link = { id: this.link.id, rev: this.conflict.serverRev, name: this.link.name, access: this.link.access };
    this.conflict = null; this.paused = false; this.emit();
    return this.save();
  }
  /** Eigenen Stand als Kopie unter neuem Namen speichern (die Kopie wird die verbundene Karte) */
  async saveAsCopy(name: string): Promise<MapMeta> { return this.createFromCurrent(name); }
  /** Serverstand laden; der eigene Stand bleibt über „Rückgängig“ erreichbar */
  async loadServer(): Promise<void> {
    if (!this.link) return;
    const id = this.link.id; this.conflict = null; this.emit();
    await this.open(id);
  }

  // ---- Folgen von Aktionen im Dialog
  renamed(id: string, meta: { name: string; rev: number }): void {
    if (this.link && this.link.id === id) { this.link = { id, rev: meta.rev, name: meta.name, access: this.link.access }; this.emit(); }
  }
  removed(id: string): void {
    if (this.link && this.link.id === id) { this.link = null; this.paused = false; this.conflict = null; this.emit(); }
  }
}
